import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { SingleValue } from "react-select";
import AsyncSelect from "react-select/async";
import { defineMessages, useIntl } from "react-intl";
import { ISectionNode } from "@web/api/Integration/types";
import { Overlay } from "@web/components";
import { ActionMenu } from "@web/elements";
import { commonTexts } from "@web/translations";
import { vars } from "@web/styles";
import { selectTheme } from "@web/styles/DMReactSelectStyle";

interface Events {
  findSections: (query: string) => Promise<ISectionNode[]>;
  onAddDone: (tag: ISectionNode) => void;
  onAddCancel: () => void;
}

interface Props extends Events {
  sectionId?: string;
}

interface SelectOption {
  label: string;
  value: ISectionNode;
  isDisabled?: boolean;
}

export const SelectSectionBox: React.FC<Props> = observer((props) => {
  const intl = useIntl();
  const [selectedSection, setSelectedSection] = useState<ISectionNode>();
  const canSave = selectedSection !== undefined;

  function handleSave(doSave: boolean) {
    const { onAddDone, onAddCancel } = props;
    if (doSave && selectedSection) {
      onAddDone(selectedSection);
    } else {
      onAddCancel();
    }
  }

  function handleSelectChange(selected: SingleValue<SelectOption>) {
    setSelectedSection(selected?.value);
  }

  async function loadOptions(inputValue: string) {
    const res = await props.findSections(inputValue);
    return res.map((tag) => ({
      value: tag,
      label: tag.title,
    }));
  }

  return (
    <Overlay
      onEscape={() => handleSave(false)}
      onClickOutside={() => handleSave(false)}
    >
      <_wrap>
        <h3>{intl.formatMessage(texts.title)}</h3>
        <_inputWrap>
          <AsyncSelect
            autoFocus
            isClearable
            theme={selectTheme}
            placeholder={intl.formatMessage(texts.typeToFindSection)}
            loadOptions={loadOptions}
            onChange={handleSelectChange}
            loadingMessage={({ inputValue }) =>
              intl.formatMessage(texts.lookingFor, {
                value: inputValue,
              })
            }
            noOptionsMessage={({ inputValue }) =>
              inputValue.length > 0
                ? intl.formatMessage(commonTexts.noMatches)
                : null
            }
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }}
          />
        </_inputWrap>

        <ActionMenu
          onApply={() => handleSave(true)}
          onCancel={() => handleSave(false)}
          applyIsDisabled={!canSave}
          applyText={commonTexts.select}
        />
      </_wrap>
    </Overlay>
  );
});

const _wrap = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  width: 350px;
  background: ${vars.content};
  color: ${vars.contentFg};
  border-radius: 3px;
  padding: 20px;
  font-size: 13px;
  input {
    border-radius: 3px;
  }
  h3 {
    margin: 0px;
    font-size: 16px;
  }
`;

const _inputWrap = styled.div`
  margin: 10px 0px;
`;

const texts = defineMessages({
  title: {
    id: "section.selectbox.title",
    defaultMessage: "Find section",
  },
  lookingFor: {
    id: "section.selectbox.lookingfor",
    defaultMessage: 'Looking for "{value}"',
  },
  typeToFindSection: {
    id: "section.selectbox.typetofindsection",
    defaultMessage: "Type to find a section…",
  },
});
